/**
 * TODO: Replace this local TabbedContent with @fluentsms/agentnet-web-components once
 * the MUI and React version upgrade is complete. AgentNetPortal currently uses Material-UI v4
 * and React 17, while lib-agentnet targets MUI v7 and React 19, so the shared component
 * cannot be used directly today.
 */
import { pxToRem, transitionSpeed } from '@fluentsms/agentnet-web-components';
import { Box, makeStyles, Paper, Tab, Tabs, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ChangeEvent, FC, ReactNode } from 'react';

export interface TabItem {
  label: string;
  id: string;
  testId?: string;
  disabled?: boolean;
}

export interface TabbedContentProps {
  tabs: TabItem[];
  children: ReactNode;
  /** Id of the selected tab. The parent owns this so the tab strip and the rendered panel cannot drift apart. */
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0, 1),
  },
  tab: {
    background: 'none',
    border: 0,
    borderBottom: '3px solid transparent',
    color: theme.palette.text.primary,
    fontSize: pxToRem(14),
    lineHeight: pxToRem(21),
    minWidth: 0,
    opacity: 1,
    padding: theme.spacing(0, 2),
    height: 52,
    textDecoration: 'none',
    textTransform: 'capitalize',
    transition: `${transitionSpeed}`,
    width: 'auto',
    '&:hover': {
      opacity: 1,
    },
    '&:not(.Mui-selected)': {
      fontWeight: 400,
    },
    '&.Mui-selected': {
      fontWeight: 600,
    },
  },
  indicator: {
    height: 3,
    backgroundColor: theme.palette.primary.main,
  },
  content: {
    padding: theme.spacing(2, 0),
  },
}));

export const TabbedContent: FC<TabbedContentProps> = ({ tabs, children, activeTab, onTabChange, className }) => {
  const classes = useStyles();

  const selectedIndex = tabs.findIndex((tab) => tab.id === activeTab);
  const activeIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const handleTabChange = (event: ChangeEvent<unknown>, newValue: number) => {
    if (tabs[newValue]?.disabled) {
      return;
    }

    onTabChange(tabs[newValue].id);
  };

  return (
    <div className={clsx(classes.root, className && className)}>
      <Paper elevation={0}>
        <Box className={classes.tabs}>
          <Tabs
            value={activeIndex}
            onChange={handleTabChange}
            aria-label="tabbed content navigation"
            classes={{ indicator: classes.indicator }}
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                label={tab.label}
                id={`tab-${tab.id}`}
                aria-controls={`tabpanel-${tab.id}`}
                classes={{ root: classes.tab }}
                disableRipple
                disabled={tab.disabled}
                data-testid={tab.testId}
              />
            ))}
          </Tabs>
        </Box>
        <Box
          className={classes.content}
          role="tabpanel"
          id={`tabpanel-${tabs[activeIndex].id}`}
          aria-labelledby={`tab-${tabs[activeIndex].id}`}
        >
          {children}
        </Box>
      </Paper>
    </div>
  );
};
